import axios from 'axios';
import { formatDate } from '@/services/DateFormatter';

/**
 * Registers a new user by sending user details to the API and storing the response data in sessionStorage.
 *
 * @param {string} email - The email address of the user to register.
 * @param {string} password - The password for the user account.
 * @param {string} name - The name of the user to register.
 * @param {string} [b_date] - The birth date of the user in a string format (optional).
 * @return {Promise<Object>} A Promise that resolves to an object containing the user's account details, token, and related metadata.
 */
export async function registerUser(email, password, name, b_date) {

    const apiUrl = process.env.VUE_APP_API_URL;

    /**
     * Sends a POST request to register a new user.
     *
     * The request is sent to the specified API URL with user details including
     * email, password, name, and birth date. The birth date is included if provided,
     * otherwise, it defaults to null.
     *
     * @type {Promise<AxiosResponse>} response - The response from the API call.
     * @property {string} apiUrl - The base URL of the API endpoint.
     * @property {string} email - The email of the user to register.
     * @property {string} password - The password for the new user account.
     * @property {string} name - The name of the user to register.
     * @property {string|null} b_date - The user's birth date, or null if not provided.
     */
    const response = await axios.post(`${apiUrl}/register`, {
        email,
        password,
        name,
        b_date: b_date || null,
    });

    /**
     * The unique identifier for a user within the system.
     * This variable represents the primary key used to distinguish each user.
     * Typically assigned by the database or generated upon user creation.
     */
    const { id_user, email: userEmail, name: registeredName, r_date, b_date: birthDate, role } = response.data.user;
    /**
     * Represents a unique identifier or authentication token.
     * Typically used for verifying user sessions, accessing secure resources,
     * or serving as a key for API requests.
     */
    const { token, ok, message } = response.data;

    /**
     * Represents user data information.
     *
     * @typedef {Object} userData
     * @property {boolean} ok - Indicates whether the operation was successful.
     * @property {number} id_user - Unique identifier for the user.
     * @property {string} email - Email address of the user.
     * @property {string|null} name - Full name of the user, or null if not provided.
     * @property {string} r_date - Registration date of the user.
     * @property {string} b_date - Birth date of the user.
     * @property {string} role - Role of the user within the system.
     * @property {string} message - Message related to the user's status or action.
     * @property {string} token - Authentication token associated with the user.
     */
    const userData = {
        ok,
        id_user,
        email: userEmail,
        name: registeredName || null,
        r_date,
        b_date: birthDate,
        role,
        message,
        token,
    };

    /*
    * Store user data in sessionStorage for later use.
    */
    sessionStorage.setItem('ok', userData.ok);
    sessionStorage.setItem('id', userData.id_user);
    sessionStorage.setItem('email', userData.email);
    sessionStorage.setItem('name', userData.name);
    sessionStorage.setItem('r_date', formatDate(userData.r_date));
    sessionStorage.setItem('b_date', userData.b_date ? formatDate(userData.b_date) : null);
    sessionStorage.setItem('role', userData.role);
    sessionStorage.setItem('message', userData.message);
    sessionStorage.setItem('token', userData.token);

    return userData;
}
